import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { clientAPI } from "@/lib/client-api";
import type { LocationFeature, LocationSection } from "@/lib/db-api";

const iconOptions = ["MapPin", "Landmark", "Waves", "Sun", "Home", "Building", "Mountain", "Ship"];

const AdminLocation = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [features, setFeatures] = useState<LocationFeature[]>([]);
  const [section, setSection] = useState<LocationSection | null>(null);
  const [editingFeature, setEditingFeature] = useState<LocationFeature | null>(null);
  const [newFeature, setNewFeature] = useState({ icon: "MapPin", title: "", description: "" });

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth !== 'true') {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = () => {
    try {
      const featureData = clientAPI.getLocationFeatures();
      const sectionData = clientAPI.getLocationSection();
      setFeatures(featureData);
      if (sectionData) setSection(sectionData);
    } catch (error) {
      console.error('Error loading location:', error);
    }
  };

  const handleAddFeature = () => {
    if (!newFeature.title || !newFeature.description) {
      toast({
        title: "Error",
        description: "Please fill all fields",
        variant: "destructive",
      });
      return;
    }

    try {
      clientAPI.createLocationFeature({
        ...newFeature,
        order_index: features.length + 1,
        active: 1,
      });
      setNewFeature({ icon: "MapPin", title: "", description: "" });
      loadData();
      toast({
        title: "Success",
        description: "Feature added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add feature",
        variant: "destructive",
      });
    }
  };

  const handleUpdateFeature = () => {
    if (!editingFeature) return;

    try {
      clientAPI.updateLocationFeature(editingFeature.id, editingFeature);
      setEditingFeature(null);
      loadData();
      toast({
        title: "Success",
        description: "Feature updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update feature",
        variant: "destructive",
      });
    }
  };

  const handleDeleteFeature = (id: number) => {
    if (confirm("Are you sure you want to delete this feature?")) {
      try {
        clientAPI.deleteLocationFeature(id);
        loadData();
        toast({
          title: "Success",
          description: "Feature deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete feature",
          variant: "destructive",
        });
      }
    }
  };

  const handleSaveSection = () => {
    if (!section) return;

    try {
      clientAPI.updateLocationSection(section);
      toast({
        title: "Success",
        description: "Location section updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update location section",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid gap-6">
          {section && (
            <Card>
              <CardHeader>
                <CardTitle>Location Section Content</CardTitle>
                <CardDescription>Edit the location section title and description</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sectionTitle">Section Title</Label>
                  <Input
                    id="sectionTitle"
                    value={section.title}
                    onChange={(e) => setSection({ ...section, title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sectionDescription">Section Description</Label>
                  <Textarea
                    id="sectionDescription"
                    value={section.description}
                    onChange={(e) => setSection({ ...section, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <Button onClick={handleSaveSection}>
                  <Save className="mr-2 h-4 w-4" />
                  Save Section
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Add New Feature</CardTitle>
              <CardDescription>Add a new location feature</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="icon">Icon</Label>
                <Select
                  value={newFeature.icon}
                  onValueChange={(value) => setNewFeature({ ...newFeature, icon: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((icon) => (
                      <SelectItem key={icon} value={icon}>
                        {icon}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newFeature.title}
                  onChange={(e) => setNewFeature({ ...newFeature, title: e.target.value })}
                  placeholder="Prime Location"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newFeature.description}
                  onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
                  placeholder="Located in the heart of Lindos village"
                  rows={2}
                />
              </div>
              <Button onClick={handleAddFeature}>
                <Plus className="mr-2 h-4 w-4" />
                Add Feature
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Location Features</CardTitle>
              <CardDescription>Manage your location features</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {features.map((feature) => (
                  <div key={feature.id} className="border p-4 rounded-lg space-y-4">
                    {editingFeature?.id === feature.id ? (
                      <>
                        <div className="space-y-2">
                          <Label>Icon</Label>
                          <Select
                            value={editingFeature.icon}
                            onValueChange={(value) => setEditingFeature({ ...editingFeature, icon: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {iconOptions.map((icon) => (
                                <SelectItem key={icon} value={icon}>
                                  {icon}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            value={editingFeature.title}
                            onChange={(e) => setEditingFeature({ ...editingFeature, title: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={editingFeature.description}
                            onChange={(e) => setEditingFeature({ ...editingFeature, description: e.target.value })}
                            rows={2}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleUpdateFeature}>
                            <Save className="mr-2 h-4 w-4" />
                            Save
                          </Button>
                          <Button variant="outline" onClick={() => setEditingFeature(null)}>
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <p className="text-sm text-muted-foreground">Icon: {feature.icon}</p>
                          <h3 className="text-lg font-semibold">{feature.title}</h3>
                          <p className="text-muted-foreground">{feature.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setEditingFeature(feature)}>
                            Edit
                          </Button>
                          <Button variant="destructive" onClick={() => handleDeleteFeature(feature.id)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminLocation;
